import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import Divider from '../../components/Divider';
import Header from '../../components/Header';
import { getFileType } from '../../utilities/parsers';
import { uploadBill, getBills, adminUpdateBill, getMedias, getBillFile } from '../../api/Api';
import dateUtils from '../../utilities/dateUtils';
import { ProfileContext } from '../../contexts/ProfileContext';
import InputWithAutocomplete from '../../components/inputs/InputWithAutocomplete';
import FromToDateInput from '../../components/inputs/FromToDateInput';
import BulmaPagination from '../../components/Pagination';
import DropdownInput from '../../components/inputs/DropDownInput';
import { useLoadingContext } from '../../contexts/LoadingContext';

const RESULTSLIMITPERPAGE = process.env.REACT_APP_API_RESULTS_LIMIT || 10;

const FileUploaderSection = ({
  handleSelectFile,
  fileToUpload,
  filePeriod,
  setFilePeriod,
  setFileDescription,
  fileDescription,
  handleUpload,
}) => {
  return (
    <div
      className="box"
      style={{
        margin: '1rem 0',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        padding: '1rem',
      }}
    >
      <h4 className="subtitle is-4 has-text-weight-bold mb-4 has-text-primary">
        <span className="icon-text">
          <span className="icon mr-2">
            <i className="fa fa-cloud-upload"></i>
          </span>
          <span>Subir Nueva Factura</span>
        </span>
      </h4>

      <div className="columns is-multiline">
        <div className="column is-6">
          <div className="field">
            <label className="label has-text-weight-semibold">
              <span className="icon-text">
                <span className="icon is-small mr-2">
                  <i className="fa fa-file"></i>
                </span>
                <span>Adjuntar Archivo</span>
              </span>
            </label>
            <div className="file has-name is-primary is-medium" style={{ width: '100%' }}>
              <label className="file-label" style={{ width: '100%' }}>
                <input
                  className="file-input"
                  type="file"
                  name="resume"
                  onChange={(e) => handleSelectFile(e)}
                />
                <span className="file-cta" style={{ borderRadius: '6px 0 0 6px' }}>
                  <span className="file-icon">
                    <i className="fas fa-upload"></i>
                  </span>
                  <span className="file-label">Elegir archivo</span>
                </span>
                <span className="file-name" style={{ borderRadius: '0 6px 6px 0', maxWidth: '60%' }}>
                  {fileToUpload.length > 0 ? fileToUpload[0]?.name : 'Ningún archivo seleccionado'}
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className="column is-6">
          <div className="field">
            <label className="label has-text-weight-semibold">
              <span className="icon-text">
                <span className="icon is-small mr-2">
                  <i className="fa fa-file-code-o"></i>
                </span>
                <span>Tipo de Archivo</span>
              </span>
            </label>
            <div className="control has-icons-left">
              <input
                className="input is-medium"
                type="text"
                disabled={true}
                value={getFileType(fileToUpload[0]?.name)}
                style={{ borderRadius: '6px', backgroundColor: '#f5f5f5' }}
              />
              <span className="icon is-small is-left">
                <i className="fa fa-file-code-o"></i>
              </span>
            </div>
          </div>
        </div>

        <div className="column is-4">
          <div className="field">
            <label className="label has-text-weight-semibold">
              <span className="icon-text">
                <span className="icon is-small mr-2">
                  <i className="fa fa-calendar"></i>
                </span>
                <span>Período</span>
              </span>
            </label>
            <div className="control has-icons-left">
              <input
                min="2019-01"
                className="input is-medium"
                type="month"
                value={filePeriod}
                onChange={(e) => {
                  e.preventDefault();
                  setFilePeriod(e.target.value);
                }}
                onKeyDown={(e) => e.preventDefault()}
                style={{ borderRadius: '6px' }}
              />
              <span className="icon is-small is-left">
                <i className="fa fa-calendar"></i>
              </span>
            </div>
          </div>
        </div>

        <div className="column is-4">
          <div className="field">
            <label className="label has-text-weight-semibold">
              <span className="icon-text">
                <span className="icon is-small mr-2">
                  <i className="fa fa-comment"></i>
                </span>
                <span>Descripción</span>
              </span>
            </label>
            <div className="control has-icons-left">
              <input
                autoFocus
                className="input is-medium"
                type="text"
                placeholder="Ingresa una descripción"
                value={fileDescription}
                onChange={(e) => {
                  e.preventDefault();
                  setFileDescription(e.target.value);
                }}
                style={{ borderRadius: '6px' }}
              />
              <span className="icon is-small is-left">
                <i className="fa fa-comment"></i>
              </span>
            </div>
          </div>
        </div>

        <div className="column is-4">
          <div className="field">
            <label className="label has-text-weight-semibold">&nbsp;</label>
            <button
              className="button is-primary is-medium is-fullwidth"
              disabled={!fileToUpload || fileToUpload.length === 0 || !filePeriod}
              onClick={handleUpload}
              style={{
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(50, 115, 220, 0.3)',
              }}
            >
              <span className="icon">
                <i className="fa fa-upload"></i>
              </span>
              <span>Subir Archivo</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BillsTable = ({ isAdmin, files, handleChangeFile, onPressSave, setLoading }) => {
  const downloadFile = async (e, file) => {
    setLoading(true);
    const response = await getBillFile(file);
    const blob = await response.blob();
    let url = window.URL.createObjectURL(blob);
    let a = document.createElement('a');
    a.href = url;
    a.download = file.file_name;
    a.click();
    setLoading(false);
  };

  const getFilePeriod = (file) => {
    const { period } = file;
    if (!period) return '';
    const date = new Date(`${period} EDT`);
    const year = date.getFullYear();
    const month = date.getMonth();
    return `${dateUtils.getMonth(month)} ${year}`;
  };

  const getFileTypeColor = (fileName) => {
    const type = getFileType(fileName).toLowerCase();
    if (type.includes('pdf')) return 'is-danger';
    if (type.includes('excel') || type.includes('xlsx')) return 'is-success';
    if (type.includes('word') || type.includes('docx')) return 'is-info';
    return 'is-light';
  };

  return (
    <div className="box" style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)', padding: '2rem' }}>
      
      <div className="table-container" style={{ borderRadius: '8px', overflow: 'auto', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
        <table className="table is-fullwidth is-striped is-hoverable" style={{ width: '100%', minWidth: '1200px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              {isAdmin && (
                <th className="has-text-weight-bold has-text-left" style={{ minWidth: '140px' }}>
                  <span className="icon-text">
                    <span className="icon is-small mr-2">
                      <i className="fa fa-tv"></i>
                    </span>
                    <span>Afiliado</span>
                  </span>
                </th>
              )}
              <th className="has-text-weight-bold has-text-left" style={{ minWidth: '160px' }}>
                <span className="icon-text">
                  <span className="icon is-small mr-2">
                    <i className="fa fa-file"></i>
                  </span>
                  <span>Archivo</span>
                </span>
              </th>
              <th className="has-text-weight-bold has-text-left" style={{ minWidth: '80px' }}>
                <span className="icon-text">
                  <span className="icon is-small mr-2">
                    <i className="fa fa-file-code-o"></i>
                  </span>
                  <span>Tipo</span>
                </span>
              </th>
              <th className="has-text-weight-bold has-text-left" style={{ minWidth: '100px' }}>
                <span className="icon-text">
                  <span className="icon is-small mr-2">
                    <i className="fa fa-calendar"></i>
                  </span>
                  <span>Período</span>
                </span>
              </th>
              <th className="has-text-weight-bold has-text-left" style={{ minWidth: '120px' }}>
                <span className="icon-text">
                  <span className="icon is-small mr-2">
                    <i className="fa fa-comment"></i>
                  </span>
                  <span>Descripción</span>
                </span>
              </th>
              {isAdmin && (
                <>
                  <th className="has-text-weight-bold has-text-center" style={{ minWidth: '80px' }}>
                    <span className="icon-text">
                      <span className="icon is-small mr-2">
                        <i className="fa fa-check-circle"></i>
                      </span>
                      <span>Pagada</span>
                    </span>
                  </th>
                  <th className="has-text-weight-bold has-text-left" style={{ minWidth: '120px' }}>
                    <span className="icon-text">
                      <span className="icon is-small mr-2">
                        <i className="fa fa-calendar-check-o"></i>
                      </span>
                      <span>Fecha Pago</span>
                    </span>
                  </th>
                  <th className="has-text-weight-bold has-text-left" style={{ minWidth: '140px' }}>
                    <span className="icon-text">
                      <span className="icon is-small mr-2">
                        <i className="fa fa-eye"></i>
                      </span>
                      <span>Observaciones</span>
                    </span>
                  </th>
                  <th className="has-text-weight-bold has-text-center" style={{ minWidth: '100px' }}>
                    <span className="icon-text">
                      <span className="icon is-small mr-2">
                        <i className="fa fa-cogs"></i>
                      </span>
                      <span>Acciones</span>
                    </span>
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {files.map((file, index) => (
              <tr 
                key={index}
                className="hover-row"
                style={{
                  transition: 'all 0.2s ease',
                  borderLeft: `4px solid ${file.is_payed ? '#48c78e' : '#f39c12'}`,
                }}
              >
                {isAdmin && (
                  <td className="is-vcentered has-text-left">
                    <span className="has-text-weight-medium has-text-dark is-size-6">
                      {file.user.media.name}
                    </span>
                  </td>
                )}
                <td className="is-vcentered has-text-left">
                  <button 
                    className="button is-link is-light is-small is-rounded"
                    onClick={(e) => downloadFile(e, file)}
                    style={{
                      transition: 'all 0.2s ease',
                      maxWidth: '140px',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                    title={file.file_name}
                  >
                    <span className="icon is-small">
                      <i className="fa fa-download"></i>
                    </span>
                    <span className="is-size-6">{file.file_name.length > 15 ? `${file.file_name.substring(0, 15)}...` : file.file_name}</span>
                  </button>
                </td>
                <td className="is-vcentered has-text-left">
                  <span className={`tag ${getFileTypeColor(file?.file_name)} is-light has-text-weight-medium is-size-6`}>
                    {getFileType(file?.file_name)}
                  </span>
                </td>
                <td className="is-vcentered has-text-left">
                  <span className="has-text-weight-medium has-text-dark is-size-6">
                    {getFilePeriod(file)}
                  </span>
                </td>
                <td className="is-vcentered has-text-left">
                  <span className="has-text-weight-medium has-text-grey-dark is-size-6" title={file.description}>
                    {file.description.length > 20 ? `${file.description.substring(0, 20)}...` : file.description}
                  </span>
                </td>
                {isAdmin && (
                  <>
                    <td className="is-vcentered has-text-centered">
                      <label className="checkbox" style={{ cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={file.is_payed}
                          onChange={(event) => handleChangeFile(index, 'is_payed', event.target.value)}
                          style={{ marginRight: '0.5rem', transform: 'scale(1.2)' }}
                        />
                        <span className={`tag ${file.is_payed ? 'is-success' : 'is-warning'} is-light is-size-6`}>
                          {file.is_payed ? 'Sí' : 'No'}
                        </span>
                      </label>
                    </td>
                    <td className="is-vcentered has-text-left">
                      <div className="control has-icons-left">
                        <input
                          type="date"
                          className="input is-normal"
                          value={file.date_of_payment || ''}
                          onChange={(event) =>
                            handleChangeFile(index, 'date_of_payment', event.target.value)
                          }
                          onKeyDown={(e) => e.preventDefault()}
                          style={{ borderRadius: '4px' }}
                        />
                        <span className="icon is-small is-left">
                          <i className="fa fa-calendar"></i>
                        </span>
                      </div>
                    </td>
                    <td className="is-vcentered has-text-left">
                      <textarea
                        className="textarea is-size-6"
                        value={file.observations || ''}
                        onChange={(event) =>
                          handleChangeFile(index, 'observations', event.target.value)
                        }
                        style={{ 
                          borderRadius: '4px', 
                          minHeight: '60px',
                          resize: 'vertical',
                        }}
                        placeholder="Observaciones..."
                      />
                    </td>
                    <td className="is-vcentered has-text-centered">
                      <button 
                        className="button is-success is-normal is-rounded"
                        onClick={() => onPressSave(file)}
                        style={{
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                      >
                        <span className="icon is-small">
                          <i className="fa fa-save"></i>
                        </span>
                        <span className='is-size-6'>Guardar</span>
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              {isAdmin && <th className="has-text-weight-bold has-text-left">Afiliado</th>}
              <th className="has-text-weight-bold has-text-left">Archivo</th>
              <th className="has-text-weight-bold has-text-left">Tipo</th>
              <th className="has-text-weight-bold has-text-left">Período</th>
              <th className="has-text-weight-bold has-text-left">Descripción</th>
              {isAdmin && (
                <>
                  <th className="has-text-weight-bold has-text-centered">Pagada</th>
                  <th className="has-text-weight-bold has-text-left">Fecha Pago</th>
                  <th className="has-text-weight-bold has-text-left">Observaciones</th>
                  <th className="has-text-weight-bold has-text-centered">Acciones</th>
                </>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
      
      {files.length === 0 && (
        <div className="has-text-centered" style={{ padding: '3rem' }}>
          <span className="icon is-large has-text-grey-light">
            <i className="fa fa-file-invoice fa-3x"></i>
          </span>
          <p className="title is-4 has-text-grey-light mt-4">No hay facturas registradas</p>
          <p className="subtitle has-text-grey">Sube tu primera factura para comenzar</p>
        </div>
      )}
    </div>
  );
};

const Filters = ({
  medias,
  applyFilter,
  filters,
  setFilters,
  clearFilters,
  setBadFromYear,
  setBadToYear,
}) => {
  const [mediaSearchTerm, setMediaSearchTerm] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const handleChangeDate = (event, field) => {
    const { value } = event.target;
    const year = parseInt(value.slice(0, 4));

    if (field === 'from') {
      if (year < 1970) {
        setBadFromYear(true);
        setFromDate(value);
      } else {
        setBadFromYear(false);
        setFromDate(value);
      }
    } else {
      if (year < 1970) {
        setBadToYear(true);
        setToDate(value);
      } else {
        setBadToYear(false);
        setToDate(value);
      }
    }
  };

  const handleClearFilters = async () => {
    setMediaSearchTerm([]);
    setFromDate('');
    setToDate('');
    await clearFilters();
  };

  return (
    <div
      className="box"
      style={{
        margin: '1rem 0',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        padding: '2rem',
      }}
    >

      <div className="columns is-multiline mb-2" style={{ marginBottom: '1rem' }}>
        <div className="column is-6">
          <div className="field">
            <InputWithAutocomplete
              title="Afiliado"
              suggestions={medias}
              searchTerm={mediaSearchTerm}
              setSeachTerm={setMediaSearchTerm}
            />
          </div>
        </div>

        <div className="column is-6">
          <div className="field">
            <DropdownInput
              title="Factura pagada?"
              options={['Indistinto', 'Si', 'No']}
              setSelectedOption={(option) => {
                setFilters({ ...filters, is_payed: option });
              }}
              selectedOption={filters.is_payed}
            />
          </div>
        </div>

        <div className="column is-12">
          <div className="field">
            <FromToDateInput
              period
              title="Período"
              fromDate={fromDate}
              toDate={toDate}
              onChangeFrom={(event) => handleChangeDate(event, 'from')}
              onChangeTo={(event) => handleChangeDate(event, 'to')}
            />
          </div>
        </div>
      </div>

      <div className="has-text-right" style={{ marginTop: '2rem' }}>
        <div className="field is-grouped is-grouped-right">
          <div className="control">
            <button
              className="button is-light is-normal"
              onClick={handleClearFilters}
              style={{
                borderRadius: '8px',
                transition: 'all 0.2s ease',
              }}
            >
              <span className="icon">
                <i className="fa fa-times-circle"></i>
              </span>
              <span>Limpiar filtros</span>
            </button>
          </div>
          <div className="control">
            <button
              className="button is-primary is-normal"
              onClick={() => applyFilter({ media: mediaSearchTerm, from: fromDate, to: toDate })}
              style={{
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0, 123, 255, 0.3)',
              }}
            >
              <span className="icon">
                <i className="fa fa-search"></i>
              </span>
              <span>Consultar</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const EmptyFiltersObject = {
  media: '',
  is_payed: undefined,
  from: '',
  to: '',
};

const BillsPage = () => {
  const [files, setFiles] = useState([]);
  const [fileToUpload, setFileToUpload] = useState([]);
  const [fileDescription, setFileDescription] = useState('');
  const [filePeriod, setFilePeriod] = useState('');
  const { setLoading } = useLoadingContext();
  const [pagination, setPagination] = useState({
    count: 0,
    limit: RESULTSLIMITPERPAGE,
    currentPage: 1,
    offset: 0,
  });
  const [medias, setMedias] = useState([]);
  const [filters, setFilters] = useState(EmptyFiltersObject);
  const { profile, isLoadingProfile } = useContext(ProfileContext);
  const { addToast } = useToasts();
  const [badFromYear, setBadFromYear] = useState(false);
  const [badToYear, setBadToYear] = useState(false);

  const fetchBills = useCallback((page = 1, options = {}) => {
    const { limit } = pagination;
    let offset;
    if (page === 1) {
      offset = 0;
    } else {
      offset = (page - 1) * limit;
    }
    setLoading(true);
    getBills(limit, offset, options).then(({ json }) => {
      const { count, results } = json;
      const newPagination = pagination;
      newPagination.count = count;
      newPagination.currentPage = page;
      setPagination(newPagination);
      setFiles(results);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const fetchMedias = async () => {
      const { json } = await getMedias('childMediasOnly=True');
      setMedias(json.map((media) => media.name));
    };
    fetchMedias();
    fetchBills();
  }, []);

  const handleSelectFile = (e) => {
    setFileToUpload(e.target.files);
  };

  const handleUpload = async () => {
    setLoading(true);
    let response;
    try {
      response = await uploadBill(fileToUpload[0], fileDescription, `${filePeriod}-01`);
    } catch (error) {
      console.log(error);
    }
    setFileToUpload([]);
    setFilePeriod('');
    setFileDescription('');
    fetchBills();
    setLoading(false);
  };

  const handleChangeFile = (index, key, value) => {
    const newFiles = [...files];
    const file = newFiles[index];
    if (key === 'is_payed') {
      file[key] = !file.is_payed;
    } else {
      file[key] = value;
    }
    if (key === 'date_of_payment') {
      file[key] = value;
      console.log('file');
    }
    setFiles(newFiles);
  };

  const onPressSave = async (file) => {
    setLoading(true);
    const { id } = file;
    if (file['date_of_payment']) {
      const date = file['date_of_payment'];
      const badYear = parseInt(date.slice(0, 4));
      if (badYear < 1970) {
        addToast('La fecha ingresado es incorrecta, por favor verifique los datos ingresados', {
          appearance: 'error',
          autoDismiss: true,
          autoDismissTimeout: 3000,
          placement: 'bottom-center',
        });
        setLoading(false);
        return;
      }
    }

    try {
      const response = await adminUpdateBill(id, file);
      addToast('Factura actualizada con exito', {
        appearance: 'success',
        autoDismiss: true,
        autoDismissTimeout: 3000,
        placement: 'bottom-center',
      });
    } catch (error) {
      console.log('Error', error);
      addToast('Ocurrio un error al tratar de actualizar la factura', {
        appearance: 'error',
        autoDismiss: true,
        autoDismissTimeout: 3000,
        placement: 'bottom-center',
      });
    }
    setLoading(false);
  };

  const onClickPageNumber = async (pageNumber) => {
    setLoading(true);
    await fetchBills(pageNumber);
    setLoading(false);
  };

  const clearFilters = async () => {
    setFilters(EmptyFiltersObject);
    setLoading(true);
    await fetchBills();
    setLoading(false);
  };

  const Pagination = () => {
    const { currentPage, count, limit } = pagination;
    return (
      <section className="section">
        <div className="columns is-centered">
          <div className="column is-half">
            <BulmaPagination
              pages={Math.ceil(count / limit)}
              currentPage={currentPage}
              onChange={(page) => onClickPageNumber(page)}
            />
          </div>
        </div>
      </section>
    );
  };

  const applyFilter = async (extraFilters = {}) => {
    const newFilters = { ...filters, ...extraFilters };
    const { from, to } = newFilters;

    if (from) {
      const fromString = dateUtils.getDjangODate(from);
      newFilters.from = fromString;
    }
    if (to) {
      const toString = dateUtils.getDjangODate(to);
      newFilters.to = toString;
    }

    if (new Date(newFilters.to) < new Date(newFilters.from)) {
      addToast('Fecha Vigencia Hasta no puede ser menor que Fecha Desde o el formato de fechas está incorrecto, por favor verifique.', {
        appearance: 'error',
        autoDismiss: true,
        autoDismissTimeout: 3000,
        placement: 'bottom-center',
      });
      setLoading(false);
      return;
    }

    if (badFromYear || badToYear) {
      addToast('Fecha Vigencia Hasta no puede ser menor que Fecha Desde o el formato de fechas está incorrecto, por favor verifique.', {
        appearance: 'error',
        autoDismiss: true,
        autoDismissTimeout: 3000,
        placement: 'bottom-center',
      });
      setLoading(false);
      return;
    }

    await fetchBills(1, newFilters);
  };

  if (isLoadingProfile) {
    return (
      <div className="section">
        <div className="container has-text-centered">
          <div className="box" style={{ borderRadius: '12px', padding: '3rem' }}>
            <span className="icon is-large has-text-primary">
              <i className="fa fa-spinner fa-pulse fa-3x"></i>
            </span>
            <p className="title is-4 mt-4">Cargando...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      
      {/* ✅ HEADER PRINCIPAL MODERNIZADO */}
      <section className="section" style={{ paddingBottom: '1rem' }}>
        <div className="container">
          <div className="columns is-centered">
            <div className="column is-10">
              <div 
                className="box has-text-centered"
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '2rem',
                  margin: '0',
                }}
              >
                <h1 className="title is-3 has-text-white has-text-weight-bold mb-3">
                  <span className="icon-text">
                    <span className="icon is-large mr-3">
                      <i className="fa fa-file-invoice fa-2x"></i>
                    </span>
                    <span>Gestión de Facturas</span>
                  </span>
                </h1>
                <p className="subtitle is-5 has-text-white-ter">
                  {profile.is_admin 
                    ? 'Administra y supervisa todas las facturas del sistema' 
                    : 'Sube y gestiona tus facturas fácilmente'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ✅ CONTENIDO PRINCIPAL */}
      <section className="section" style={{ paddingTop: '0' }}>
        <div className="container is-fluid" style={{ maxWidth: '95%' }}>
          {/* Filtros para Admin */}
          {profile.is_admin && (
            <Filters
              filters={filters}
              setFilters={setFilters}
              medias={medias}
              applyFilter={applyFilter}
              clearFilters={clearFilters}
              setBadFromYear={setBadFromYear}
              setBadToYear={setBadToYear}
            />
          )}
          
          {/* Subida de archivos para usuarios normales */}
          {!profile.is_admin && (
            <FileUploaderSection
              handleSelectFile={handleSelectFile}
              fileToUpload={fileToUpload}
              filePeriod={filePeriod}
              setFilePeriod={setFilePeriod}
              setFileDescription={setFileDescription}
              fileDescription={fileDescription}
              handleUpload={handleUpload}
            />
          )}
          
          {/* Tabla de facturas */}
          <BillsTable
            isAdmin={profile.is_admin}
            files={files}
            handleChangeFile={handleChangeFile}
            onPressSave={onPressSave}
            setLoading={setLoading}
          />
        </div>
      </section>

      {/* ✅ PAGINACIÓN */}
      <Pagination />

      {/* ✅ ESTILOS CSS ADICIONALES */}
      <style jsx>{`
        .hover-row:hover {
          background-color: rgba(0, 0, 0, 0.05) !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        .table th {
          background-color: #f8f9fa;
          border-bottom: 2px solid #dee2e6;
          padding: 1rem 0.75rem;
          vertical-align: middle;
        }

        .table td {
          padding: 1rem 0.75rem;
          vertical-align: middle;
        }

        .table-container {
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .tag {
          font-weight: 600;
          border-radius: 20px;
        }

        .box {
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .field .label {
          color: #4a4a4a;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .input, .textarea {
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .input:focus, .textarea:focus {
          box-shadow: 0 0 0 3px rgba(50, 115, 220, 0.25);
        }

        .file-label {
          transition: all 0.2s ease;
        }

        .file-label:hover {
          transform: translateY(-1px);
        }

        .columns {
          margin-bottom: 0.5rem !important;
        }

        .column {
          padding: 0.5rem !important;
        }

        .icon-text {
          align-items: center;
        }

        .checkbox {
          display: flex;
          align-items: center;
        }

        /* Tabla más compacta y responsive */
        .table {
          font-size: 0.85rem;
        }

        .table td, .table th {
          padding: 0.5rem 0.4rem;
          white-space: nowrap;
        }

        .table .textarea {
          min-height: 50px !important;
          font-size: 0.8rem;
        }

        .table .input.is-small {
          font-size: 0.8rem;
        }

        .table .button.is-small {
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
        }

        /* Responsive mejorado */
        @media screen and (max-width: 1200px) {
          .table-container {
            overflow-x: auto;
            max-width: 100%;
          }
          
          .table {
            min-width: 1100px;
          }
        }

        /* Mejoras en la accesibilidad */
        .button:focus,
        .input:focus,
        .textarea:focus {
          box-shadow: 0 0 0 3px rgba(50, 115, 220, 0.25);
        }

        /* Animaciones suaves */
        .box,
        .button,
        .hover-row,
        .input,
        .textarea,
        .file-label {
          transition: all 0.3s ease;
        }

        /* Estados disabled */
        .button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .button:disabled:hover {
          transform: none;
          box-shadow: none;
        }
      `}</style>
    </div>
  );
};

export default BillsPage;